// Can these two mantles actually be run as one pass?
//
// On a two-extruder line (L120) the interior and exterior sheath go on in a
// single pass, so `fuseTandemInteriorExterior` collapses the interior task INTO
// the exterior one: the exterior consumes the rope directly, carries both PVC
// layers, and the interior task, its token and its phase are deleted.
//
// The fusion pairs the two by TOKEN, not by position: an interior task is fused
// into the exterior task that lists its output token as an input. That link
// comes from the wizard — each exterior execution carries `fromUpstreamId`, the
// interior execution it was planned against.
//
// Which means the pairing is only as good as that wiring, and the wizard will
// happily produce a shape where it does not hold. Choose a different drum type
// on the interior and it splits into a different number of executions than the
// exterior: six interior drums of 5 000 against three exterior drums of 10 000.
// Three interior executions then have no exterior execution pointing at them.
//
// What happens next is the part worth preventing. Fusion deletes the interior
// tasks it DID match and leaves the rest standing, so the job comes out with
// orphaned interior tasks, an interior phase that never reaches 100 %, and a
// Gantt bar that never closes. Nothing says so at the time.
//
// In tandem the interior drum does not physically exist — the cable runs
// straight through both heads onto the exterior drum — so its drum type is
// decoration. Its LENGTHS are not: they are what the pairing is built from.

// The three shapes that say "length". A tandem's exterior can be wound as
// COILS ON THE MACHINE, and that shape carries no `plannedLength` at all —
// `coils_on_machine` sets it to `''` on purpose. See helpers/execLength.js.
import { execPlannedLength } from './execLength.js';

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const INT = 'interior_sheat';
const EXT = 'exterior_sheat';

/** Executions of a phase, with the id defaulting the same way the wizard does. */
export const executionsOf = (product, phaseName) => {
  const phase = product?.phases?.find(p => p?.name === phaseName);
  if (!phase) return [];
  const raw = Array.isArray(phase.execution)
    ? phase.execution
    : (phase.conductors || []).flatMap(c => c?.execution || []);
  return raw.map((e, idx) => ({ ...e, id: e?.id ?? idx + 1 }));
};

/**
 * The length an execution is planned for, in any of the three shapes.
 *
 * `plannedLength` is not one of them when the exterior is wound as coils on the
 * line: `coils_on_machine` CLEARS it (`next.plannedLength = ''`) and states the
 * run as `coilCount × coilLength` instead. This read only the two direct fields,
 * so a CYYF 5x4 whose exterior was 30 × 100 m coils + 1 000 + 1 000 added up to
 * 2 000 m against an interior of 5 000, and the tandem was refused as "3,000 m
 * apart" — the submit button did nothing and nothing said why. Reported
 * 2026-10-27; the server had already learned this shape (the comment in
 * helpers/execLength.js is about the same coils), the guard had not.
 *
 * `phaseName` is needed because the shape is phase-dependent: the same
 * `coilCount` on a `to_coiling` execution means coils some LATER machine will
 * wind, and this run's length is the cable's.
 */
export const execLength = (e, phaseName) =>
  execPlannedLength(e, phaseName) || num(e?.plannedLengthInput);

/** Upstream execution ids this execution was planned against. */
export const upstreamIdsOf = e => {
  if (Array.isArray(e?.fromUpstreamIds) && e.fromUpstreamIds.length) {
    return e.fromUpstreamIds.map(String);
  }
  if (e?.fromUpstreamId != null) return [String(e.fromUpstreamId)];
  return [];
};

/** Both mantles present — the pair exists at all. */
export const hasTandemPair = product =>
  !!product?.phases?.some(x => x?.name === INT) &&
  !!product?.phases?.some(x => x?.name === EXT);

/** The one machine a phase is planned on, or null when it is none or several. */
export const soleMachineOf = (product, phaseName) => {
  const ids = [
    ...new Set(
      executionsOf(product, phaseName)
        .map(e => String(e?.machineId || '').trim())
        .filter(Boolean)
    )
  ];
  return ids.length === 1 ? ids[0] : null;
};

/**
 * Everything that would stop this product fusing, in the order it matters.
 *
 * @returns {{ ok, problems: string[], interior, exterior }} — `problems` are
 *          finished sentences, because they are shown to the person who has to
 *          fix them and "count mismatch" is not something anyone can act on.
 */
export function checkTandemPair(product) {
  const problems = [];
  const intExecs = executionsOf(product, INT);
  const extExecs = executionsOf(product, EXT);

  const interior = {
    count: intExecs.length,
    total: intExecs.reduce((s, e) => s + execLength(e, INT), 0),
    machine: soleMachineOf(product, INT)
  };
  const exterior = {
    count: extExecs.length,
    // The phase name matters: `coils_on_machine` is only a length shape ON the
    // exterior sheath.
    total: extExecs.reduce((s, e) => s + execLength(e, EXT), 0),
    machine: soleMachineOf(product, EXT)
  };

  if (!hasTandemPair(product)) {
    return { ok: false, problems: ['this product has no interior + exterior pair'], interior, exterior };
  }
  if (!intExecs.length || !extExecs.length) {
    problems.push('one of the two mantles has no execution planned yet');
    return { ok: false, problems, interior, exterior };
  }

  // One physical line, so one machine, and the same one.
  if (!interior.machine || !exterior.machine) {
    problems.push('both mantles must be planned on a single machine each');
  } else if (interior.machine !== exterior.machine) {
    problems.push(
      `the interior runs on ${interior.machine.toUpperCase()} and the exterior on ` +
        `${exterior.machine.toUpperCase()} — a tandem is one line`
    );
  }

  // Every interior drum must be claimed by exactly one exterior drum. The
  // fusion supports several interior drums feeding one exterior drum, so the
  // counts may differ; what it cannot survive is an interior drum nobody
  // claims, or one claimed twice.
  const claims = new Map();
  for (const e of extExecs) {
    for (const up of upstreamIdsOf(e)) {
      claims.set(up, (claims.get(up) || 0) + 1);
    }
  }
  const unclaimed = intExecs.filter(e => !claims.has(String(e.id)));
  const twice = intExecs.filter(e => (claims.get(String(e.id)) || 0) > 1);

  if (unclaimed.length === intExecs.length) {
    problems.push(
      'no exterior drum is fed by an interior one — pick the interior drum as ' +
        'the upstream of each exterior drum'
    );
  } else if (unclaimed.length) {
    problems.push(
      `${unclaimed.length} of ${intExecs.length} interior drums feed nothing. They ` +
        `would be left behind as tasks nobody can finish, and the interior phase ` +
        `would never reach 100%`
    );
  }
  if (twice.length) {
    problems.push(`${twice.length} interior drum(s) are claimed by more than one exterior drum`);
  }

  // Same cable through both heads, so the same metres. Half a metre of
  // rounding is not a mismatch; a drum is.
  const gap = Math.abs(interior.total - exterior.total);
  if (gap > 0.5) {
    problems.push(
      `the interior adds up to ${interior.total.toLocaleString()} m and the exterior to ` +
        `${exterior.total.toLocaleString()} m — ${gap.toLocaleString()} m apart. ` +
        `In one pass they are the same metres`
    );
  }

  return { ok: problems.length === 0, problems, interior, exterior };
}
